import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Clock, Calendar, Globe, Shield, CheckCircle, 
  FileText, ChevronDown, ChevronUp, AlertCircle, ArrowRight,
  Star, Info, Eye
} from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './VisaDetails.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VisaDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/visa-products/by-slug/${slug}`);
      setProduct(res.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      navigate('/apply-online');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="visa-details-page">
        <Header />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) return null;

  const visaInfoCards = [
    { icon: FileText, label: 'Visa Type', value: product.visa_type },
    { icon: Calendar, label: 'Validity', value: product.validity },
    { icon: Clock, label: 'Stay Duration', value: product.length_of_stay },
    { icon: Globe, label: 'Entry Type', value: product.entry_type },
  ];

  return (
    <div className="visa-details-page">
      <Header />
      
      {/* Hero Section */}
      <section className="visa-hero">
        <div className="visa-hero-bg">
          <img src={product.hero_image} alt={product.country_name} />
          <div className="visa-hero-overlay"></div>
        </div>
        <div className="visa-hero-content">
          <button className="back-btn" onClick={() => navigate('/apply-online')}>
            <ArrowLeft size={18} />
            <span>All Destinations</span>
          </button>
          
          <div className="visa-hero-main">
            <div className="country-badge">
              <img src={product.flag_url} alt={product.country_name} />
              <span>{product.country_name}</span>
            </div>
            <h1>{product.visa_type}</h1>
            <p className="visa-short-desc">{product.short_description}</p>
            
            <div className="hero-stats">
              <div className="hero-stat">
                <Eye size={18} />
                <span>{product.visas_processed?.toLocaleString()}+</span>
              </div>
              <div className="hero-stat">
                <Star size={18} />
                <span>{product.success_rate}% Success Rate</span>
              </div>
              <div className="hero-stat">
                <Clock size={18} />
                <span>{product.processing_time}*</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="visa-content">
        <div className="visa-content-grid">
          {/* Left Column - Details */}
          <div className="visa-main-content">
            {/* Visa Info Cards */}
            <section className="visa-info-section">
              <h2>Visa Information</h2>
              <div className="visa-info-grid">
                {visaInfoCards.map((card, index) => (
                  <div key={index} className="visa-info-card">
                    <card.icon size={20} />
                    <div>
                      <span className="info-label">{card.label}</span>
                      <span className="info-value">{card.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Description */}
            <section className="visa-description-section">
              <h2>About This Visa</h2>
              <p>{product.full_description}</p>
            </section>

            {/* Documents Checklist */}
            <section className="visa-documents-section">
              <h2>Required Documents</h2>
              <div className="documents-list">
                {product.documents_checklist?.map((doc, index) => (
                  <div key={index} className="document-item">
                    <CheckCircle size={18} />
                    <span>{doc}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Important Notes */}
            {product.important_notes?.length > 0 && (
              <section className="visa-notes-section">
                <h2>Important Notes</h2>
                <div className="notes-list">
                  {product.important_notes.map((note, index) => (
                    <div key={index} className="note-item">
                      <Info size={18} />
                      <span>{note}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* FAQs */}
            {product.faqs?.length > 0 && (
              <section className="visa-faq-section">
                <h2>Frequently Asked Questions</h2>
                <div className="faq-list">
                  {product.faqs.map((faq, index) => (
                    <div 
                      key={index} 
                      className={`faq-item ${openFaq === index ? 'open' : ''}`}
                    >
                      <button 
                        className="faq-question"
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      >
                        <span>{faq.question}</span>
                        {openFaq === index ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                      {openFaq === index && (
                        <div className="faq-answer">
                          <p>{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right Column - CTA Card */}
          <div className="visa-sidebar">
            <div className="apply-card">
              <div className="apply-card-header">
                <h3>Ready to Apply?</h3>
                <p>Start your visa application now</p>
              </div>
              
              <div className="apply-card-info">
                <div className="apply-info-row">
                  <span>Processing Time</span>
                  <strong>{product.processing_time}</strong>
                </div>
                <div className="apply-info-row">
                  <span>Success Rate</span>
                  <strong>{product.success_rate}%</strong>
                </div>
              </div>

              <button 
                className="apply-now-btn"
                onClick={() => navigate(`/apply/${slug}/form`)}
              >
                Start Application
                <ArrowRight size={18} />
              </button>

              <div className="apply-card-footer">
                <Shield size={16} />
                <span>Expert guidance throughout</span>
              </div>
            </div>

            {/* Embassy Disclaimer */}
            <div className="sidebar-disclaimer">
              <AlertCircle size={16} />
              <p>{product.embassy_disclaimer}</p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VisaDetails;
